import React, { FC } from 'react';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import { HistoricalOrderAttachment } from 'models/historical-order';
import { formatBytes } from 'utils/format';
import InfoText from 'components/shared/info-text.component';

/**
 * DD-3687: the files copied in with an archived order.
 *
 * Download links are minted one at a time by the caller rather than fetched
 * with the order: a signed URL expires, and issuing one per attachment up
 * front would hand out links for files nobody opens.
 */

type HistoricalOrderFilesProps = {
    attachments: HistoricalOrderAttachment[];
    onDownload: (fileId: string) => void;
};

const HistoricalOrderFiles: FC<HistoricalOrderFilesProps> = ({ attachments, onDownload }) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    if (attachments.length === 0) {
        return <InfoText text={translate('orders:HISTORICAL_NO_FILES')} />;
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableBody>
                    {attachments.map((attachment, attachmentIndex) => (
                        <TableRow key={attachment._id} hover>
                            <TableCell>{attachment.fileName}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatBytes(attachment.size)}</TableCell>
                            <TableCell align="right">
                                <Tooltip title={translate('common:DOWNLOAD').toString()}>
                                    <IconButton
                                        color="primary"
                                        onClick={() => onDownload(attachment._id)}
                                        data-test={`historical-order-detail.download-${attachmentIndex}`}
                                    >
                                        <FileDownloadIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default HistoricalOrderFiles;
